/**
 * Meta Pixel — Core infrastructure
 *
 * Contains: pixel init, event dispatcher, Advanced Matching,
 * PageView tracking, test-client guard, and shared helpers.
 */

import {
  getAttribution,
  generateEventId,
  sha256,
  normalizePhoneE164,
  type AttributionData,
} from "./metaAttribution";

// Pixel ID
const PIXEL_ID =
  (import.meta.env.VITE_META_PIXEL_ID as string | undefined) ??
  "2222910424899708";

/* ------------------------------------------------------------------ */
/*  Standard Meta event names                                          */
/* ------------------------------------------------------------------ */
export const MetaEvents = {
  // Standard events
  PageView: "PageView",
  ViewContent: "ViewContent",
  Lead: "Lead",
  CompleteRegistration: "CompleteRegistration",
  Schedule: "Schedule",
  InitiateCheckout: "InitiateCheckout",
  Contact: "Contact",
  AddToCart: "AddToCart",
  Purchase: "Purchase",

  // Custom events (REA-specific)
  ViewProject: "ViewProject",
  ViewUnit: "ViewUnit",
  CreateQuotation: "CreateQuotation",
  DownloadPDFPresentation: "DownloadPDFPresentation",
  GenerateLead: "GenerateLead",
} as const;

export type MetaEventName = (typeof MetaEvents)[keyof typeof MetaEvents];

/* ------------------------------------------------------------------ */
/*  Enriched params interface                                          */
/* ------------------------------------------------------------------ */
export interface MetaEventParams {
  // Standard Meta params
  content_name?: string;
  content_category?: string;
  content_type?: string;
  content_ids?: string[];
  value?: number;
  currency?: string;
  num_items?: number;

  // REA custom params (safe, non-PII)
  project_name?: string;
  unit_type?: string;
  price_uf?: number;
  comuna?: string;
  lead_source?: string;
  lead_quality?: string;
  income_range?: string;

  // Deduplication
  eventID?: string;

  [key: string]: unknown;
}

/* ------------------------------------------------------------------ */
/*  Core helpers                                                       */
/* ------------------------------------------------------------------ */

const STANDARD_EVENTS = new Set([
  "PageView",
  "ViewContent",
  "Lead",
  "CompleteRegistration",
  "Schedule",
  "InitiateCheckout",
  "Contact",
  "Purchase",
  "AddToCart",
  "Search",
]);

function isFbqReady(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.fbq === "function"
  );
}

/* ------------------------------------------------------------------ */
/*  Test-client guard — centralised kill-switch for all Meta tracking   */
/* ------------------------------------------------------------------ */

/**
 * Context object set by the consuming component before firing events.
 * When `isTest` is true ALL browser-side Meta events are suppressed.
 */
let _testContext: { isTest: boolean } = { isTest: false };

/**
 * Call this before any tracking call that involves a specific client/lead.
 * E.g. `setMetaTestContext({ isTest: client.is_test })`
 */
export function setMetaTestContext(ctx: { isTest: boolean }): void {
  _testContext = ctx;
}

/** Returns true if tracking should be blocked (test client OR non-production env) */
export function isTrackingBlocked(): boolean {
  // Block in non-production environments
  if (import.meta.env.DEV) {
    console.log("[MetaPixel] 🚫 BLOCKED (DEV environment) — no tracking sent");
    return true;
  }
  return _testContext.isTest;
}


/**
 * Fire a standard or custom event with event_id for deduplication.
 * Returns the event_id used (for persisting in DB).
 */
export function trackEvent(
  eventName: MetaEventName | string,
  params?: MetaEventParams
): string {
  const eventId = params?.eventID || generateEventId();

  if (isTrackingBlocked()) {
    if (import.meta.env.DEV) {
      console.log(`[MetaPixel] 🚫 BLOCKED (test client): ${eventName}`, { eventId });
    }
    return eventId;
  }

  if (!isFbqReady()) return eventId;

  const enrichedParams = {
    ...params,
    eventID: eventId,
  };

  if (STANDARD_EVENTS.has(eventName)) {
    window.fbq?.("track", eventName, enrichedParams, {
      eventID: eventId,
    });
  } else {
    window.fbq?.("trackCustom", eventName, enrichedParams, {
      eventID: eventId,
    });
  }

  return eventId;
}

/** Fire PageView — idempotent per route */
let lastTrackedPath = "";
export function trackPageView(): void {
  if (!isFbqReady()) return;
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath === lastTrackedPath) return;
  lastTrackedPath = currentPath;

  const attribution = getAttribution();
  const eventId = generateEventId();

  window.fbq?.("track", "PageView", {}, { eventID: eventId });

  // Log for debugging
  if (import.meta.env.DEV) {
    console.log("[MetaPixel] PageView", { eventId, path: currentPath, attribution });
  }
}

/* ------------------------------------------------------------------ */
/*  Advanced Matching — called during init                             */
/* ------------------------------------------------------------------ */

/**
 * Update Advanced Matching data when user provides PII (e.g. form submit).
 * Hashes email/phone with SHA-256 before sending to Meta.
 */
export async function updateAdvancedMatching(userData: {
  email?: string;
  phone?: string;
  externalId?: string;
}): Promise<void> {
  if (!isFbqReady() || !PIXEL_ID) return;

  const advancedMatchingData: Record<string, string> = {};

  if (userData.email) {
    advancedMatchingData.em = await sha256(userData.email.toLowerCase().trim());
  }
  if (userData.phone) {
    const e164 = normalizePhoneE164(userData.phone);
    if (e164) {
      advancedMatchingData.ph = await sha256(e164);
    }
  }
  if (userData.externalId) {
    advancedMatchingData.external_id = await sha256(userData.externalId);
  }

  if (Object.keys(advancedMatchingData).length > 0) {
    // Re-init pixel with user data for Advanced Matching
    window.fbq?.("init", PIXEL_ID, advancedMatchingData);

    if (import.meta.env.DEV) {
      console.log("[MetaPixel] Advanced Matching updated", Object.keys(advancedMatchingData));
    }
  }
}

/* ------------------------------------------------------------------ */
/*  Initialize Pixel (call once at app boot)                           */
/* ------------------------------------------------------------------ */
export function initMetaPixel(): void {
  if (typeof window === "undefined") return;
  if (!PIXEL_ID) return;
  if (window.fbq) return;

  const f: any = window;
  const n: any = (f.fbq = function (...args: any[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  });
  if (!f._fbq) f._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  // Init pixel (without AM initially — AM is set per-event via updateAdvancedMatching)
  (window.fbq as Fbq | undefined)?.("init", PIXEL_ID);

  // noscript fallback
  const noscript = document.createElement("noscript");
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
}

/* ------------------------------------------------------------------ */
/*  Value estimation from income range                                 */
/* ------------------------------------------------------------------ */

const INCOME_RANGE_VALUE_PROXY: Record<string, number> = {
  "<1.5MM": 30,
  "1.5-2.0MM": 50,
  "2.0-2.5MM": 80,
  "2.5-3.0MM": 120,
  ">3.0MM": 180,
};

export function estimateLeadValue(incomeRange?: string): number {
  if (!incomeRange) return 50; // default
  return INCOME_RANGE_VALUE_PROXY[incomeRange] ?? 50;
}

/* ------------------------------------------------------------------ */
/*  Enriched event params builder (shared by Schedule/Contact/CQ)      */
/* ------------------------------------------------------------------ */

export interface EnrichedEventOpts {
  email?: string;
  phone?: string;
  externalId?: string;
  projectId?: string;
  projectName?: string;
  leadSource?: string;
  valueProxy?: number;
  currency?: string;
  numItems?: number;
}

export function buildEnrichedParams(
  eventId: string,
  attribution: AttributionData,
  opts: EnrichedEventOpts
): MetaEventParams {
  return {
    eventID: eventId,
    content_ids: opts.projectId ? [opts.projectId] : undefined,
    project_name: opts.projectName,
    lead_source: opts.leadSource,
    value: opts.valueProxy,
    currency: opts.currency || "CLF",
    num_items: opts.numItems,
    // Attribution context
    ...(attribution.utm_source && { utm_source: attribution.utm_source }),
    ...(attribution.utm_medium && { utm_medium: attribution.utm_medium }),
    ...(attribution.utm_campaign && { utm_campaign: attribution.utm_campaign }),
  };
}
